import React from 'react';
import { useTranslation } from 'react-i18next';

const MAX_VISIBLE_ROWS = 100;

const messagesFor = (item, t) => {
  // Insertion failures carry raw database errors (SQL, columns, values): never show them to the user
  if (item?.stage === 'insertion') return [t('Import row could not be saved')];
  const value = item?.messages ?? item?.errors ?? item?.error;
  if (Array.isArray(value)) return value.flatMap((message) => typeof message === 'string' ? [message] : []);
  if (value && typeof value === 'object') return Object.values(value).flat().filter((message) => typeof message === 'string');
  return typeof value === 'string' ? [value] : [];
};

const IDENTITY_KEYS = ['email', 'user_name', 'name', 'code', 'mobile'];

// Rejected rows carry the row's own values, not a spreadsheet line number, so the row is shown by its identifying values
const identityFor = (item) => {
  const row = item?.row && typeof item.row === 'object' ? item.row : {};
  const isShown = (value) => (typeof value === 'string' && value.trim() !== '') || typeof value === 'number';
  const known = IDENTITY_KEYS.map((key) => row[key]).filter(isShown);
  const values = known.length ? known : Object.values(row).filter(isShown).slice(0, 2);
  return values.join(' · ');
};

const ImportRejectedRows = ({ data, summary }) => {
  const { t } = useTranslation();
  const rows = Array.isArray(data?.rejected_rows) ? data.rejected_rows : [];
  const visibleRows = rows.slice(0, MAX_VISIBLE_ROWS);
  const total = Number(data?.rejected_rows_count ?? data?.rejected_count ?? rows.length);

  return (
    <div style={{ width: 'min(420px, 80vw)' }}>
      <div className="fw-bolder">{summary}</div>
      {visibleRows.length > 0 && (
        <>
          <div className="mt-50 fw-bolder">{t('Import rejected rows')}</div>
          <div className="border rounded mt-50" style={{ maxHeight: '220px', overflowY: 'auto' }}>
            <table className="table table-sm mb-0">
              <thead className="table-light">
                <tr>
                  <th scope="col">{t('Import row')}</th>
                  <th scope="col">{t('Import reasons')}</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((item, index) => {
                  const identity = identityFor(item);
                  const messages = messagesFor(item, t);
                  return (
                    <tr key={index}>
                      <td style={{ overflowWrap: 'anywhere' }}>{identity || t('Import row unavailable')}</td>
                      <td style={{ overflowWrap: 'anywhere' }}>
                        {messages.length ? messages.map((message, messageIndex) => (
                          <div key={messageIndex}>{message}</div>
                        )) : t('Import reason unavailable')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {total > visibleRows.length && (
            <small className="d-block mt-50">
              {t('Import showing rows', { shown: visibleRows.length, total })}
            </small>
          )}
        </>
      )}
    </div>
  );
};

export default ImportRejectedRows;
